package io.github.erkko68.filament.compose.scene.primitives

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import io.github.erkko68.filament.Box
import io.github.erkko68.filament.compose.EntityScope
import io.github.erkko68.filament.MaterialInstance
import io.github.erkko68.filament.compose.FilamentSceneScope
import io.github.erkko68.filament.compose.scene.Position
import io.github.erkko68.filament.compose.scene.Rotation
import io.github.erkko68.filament.compose.scene.Scale

/**
 * A unit cube centered on the origin (in mesh space). Six faces, 24 vertices (4 per face so
 * normals and UVs are not shared across edges), 12 triangles.
 *
 * Each face's UV maps the full 0..1 square. The cube is sized via [size] (full edge length).
 *
 * @param material  The material to apply to every face, or null while it is still loading
 *   (nothing renders until it arrives). Use [rememberMaterial] + [rememberMaterialInstance].
 * @param size      Edge length in mesh space. The mesh is rebuilt when this changes.
 * @param position  World-space position of the [pivot] point.
 * @param rotation  World-space rotation. Build one with [Rotation.axisAngle]/[Rotation.euler].
 * @param scale     Per-axis scale applied after [size].
 * @param pivot     Point in mesh space that rotation/scale revolve around and that ends up at
 *   [position]. Defaults to the cube centre.
 * @param visible   Whether this renderable is in the scene. False removes it from the
 *   scene (cheaply, keeping the entity alive) — a show/hide toggle without losing state.
 * @param castShadows     Whether the cube casts shadows onto other renderables. On by default.
 * @param receiveShadows  Whether the cube receives shadows cast by others. On by default.
 * @param onCreate  Runs once when the cube enters the scene, with the renderable entity and engine in scope ([EntityScope]).
 * @param onTap     Called when a tap on a [io.github.erkko68.filament.compose.FilamentView] hits this object, with the world point it hit. Null
 *   answers with the enclosing [io.github.erkko68.filament.compose.scene.Group]'s `onTap`, if any.
 *   Use it to register the cube with `view.pick` callbacks or other entity-keyed maps.
 */
@Composable
fun FilamentSceneScope.Cube(
    material: MaterialInstance?,
    size: Float = 1f,
    position: Position = Position(0f),
    rotation: Rotation = Rotation.Identity,
    scale: Scale = Scale(1f),
    pivot: Position = Position(0f),
    visible: Boolean = true,
    castShadows: Boolean = true,
    receiveShadows: Boolean = true,
    onCreate: EntityScope.() -> Unit = {},
    onTap: ((Position) -> Unit)? = null,
) {
    val mesh = remember(size) { cubeMesh(size) }
    Mesh(mesh, material, position, rotation, scale, pivot, visible, castShadows, receiveShadows, onCreate, onTap)
}

private fun cubeMesh(size: Float): MeshData {
    val h = size * 0.5f

    // Per-face: 4 corners CCW when viewed from outside, normal pointing outward, UV (u,v) ∈ [0,1].
    // Order: +X, -X, +Y, -Y, +Z, -Z.
    val positions = floatArrayOf(
        // +X
         h, -h,  h,    h, -h, -h,    h,  h, -h,    h,  h,  h,
        // -X
        -h, -h, -h,   -h, -h,  h,   -h,  h,  h,   -h,  h, -h,
        // +Y
        -h,  h,  h,    h,  h,  h,    h,  h, -h,   -h,  h, -h,
        // -Y
        -h, -h, -h,    h, -h, -h,    h, -h,  h,   -h, -h,  h,
        // +Z
        -h, -h,  h,    h, -h,  h,    h,  h,  h,   -h,  h,  h,
        // -Z
         h, -h, -h,   -h, -h, -h,   -h,  h, -h,    h,  h, -h,
    )

    val normals = FloatArray(24 * 3)
    val faceNormals = floatArrayOf(
         1f, 0f, 0f,   -1f,  0f,  0f,
         0f, 1f, 0f,    0f, -1f,  0f,
         0f, 0f, 1f,    0f,  0f, -1f,
    )
    for (face in 0 until 6) {
        for (v in 0 until 4) {
            val base = (face * 4 + v) * 3
            normals[base]     = faceNormals[face * 3]
            normals[base + 1] = faceNormals[face * 3 + 1]
            normals[base + 2] = faceNormals[face * 3 + 2]
        }
    }

    val uvs = FloatArray(24 * 2)
    val uvPattern = floatArrayOf(0f, 0f,  1f, 0f,  1f, 1f,  0f, 1f)
    for (face in 0 until 6) {
        for (v in 0 until 4) {
            uvs[(face * 4 + v) * 2]     = uvPattern[v * 2]
            uvs[(face * 4 + v) * 2 + 1] = uvPattern[v * 2 + 1]
        }
    }

    // Two triangles per face (0,1,2) (0,2,3) with CCW winding.
    val indices = IntArray(36)
    for (face in 0 until 6) {
        val base = face * 4
        val out = face * 6
        indices[out]     = base
        indices[out + 1] = base + 1
        indices[out + 2] = base + 2
        indices[out + 3] = base
        indices[out + 4] = base + 2
        indices[out + 5] = base + 3
    }

    return MeshData(
        positions   = positions,
        normals     = normals,
        uvs         = uvs,
        indices     = indices,
        boundingBox = Box(floatArrayOf(0f, 0f, 0f), floatArrayOf(h, h, h)),
    )
}
